import React, { useEffect, useState } from "react";
import { Box, HStack, Spinner, Text } from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";

import { getPartnersStatistics } from "../../../api/partnersApi";

const MONTH_NAMES_FR = [
  "Jan",
  "Fév",
  "Mar",
  "Avr",
  "Mai",
  "Juin",
  "Juil",
  "Août",
  "Sep",
  "Oct",
  "Nov",
  "Déc",
];

const formatMonthLabel = (monthKey, locale = "fr") => {
  if (!monthKey || !monthKey.includes("-")) return monthKey;
  const [yearStr, monthStr] = monthKey.split("-");
  const monthIdx = parseInt(monthStr, 10) - 1;
  if (locale.startsWith("en")) {
    try {
      const d = new Date(parseInt(yearStr, 10), monthIdx, 1);
      return d.toLocaleString("en-US", { month: "short" });
    } catch {
      return monthKey;
    }
  }
  return MONTH_NAMES_FR[monthIdx] || monthKey;
};

const PartnersMonthlyChart = ({ scope, buId, rangeMonths = 6 }) => {
  const { t, i18n } = useTranslation("portal");
  const [buckets, setBuckets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await getPartnersStatistics(
          { scope, rangeMonths },
          { buId: buId || undefined }
        );
        if (!cancelled) {
          setBuckets(data.buckets || []);
        }
      } catch {
        if (!cancelled) setBuckets([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [scope, buId, rangeMonths]);

  const chartData = buckets.map((b) => ({
    month: formatMonthLabel(b.month, i18n.language || "fr"),
    count: b.count,
  }));
  const total = buckets.reduce((sum, b) => sum + (b.count || 0), 0);

  return (
    <Box borderWidth="1px" borderRadius="xl" bg="white" p={4} mb={4}>
      <HStack justify="space-between" align="center" mb={3}>
        <Box>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color="gray.900"
            sx={{ marginBottom: "0 !important" }}
          >
            {t("partners.stats.chartTitle", "Monthly Activity")}
          </Text>
          <Text
            fontSize="xs"
            color="gray.500"
            sx={{ marginBottom: "0 !important" }}
          >
            {t("partners.stats.total", { count: total, defaultValue: "Total: {{count}}" })}
          </Text>
        </Box>
        {isLoading && <Spinner size="sm" color="teal.500" />}
      </HStack>

      {total > 0 ? (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
            <RechartsTooltip />
            <Bar
              dataKey="count"
              fill="#7C3AED"
              radius={[4, 4, 0, 0]}
              name={t("partners.stats.barLabel", "Count")}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box
          h="160px"
          borderRadius="lg"
          bg="gray.50"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={6}
        >
          <Text fontSize="sm" color="gray.500" textAlign="center">
            {t("partners.stats.noData", "No activity yet for this period")}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default PartnersMonthlyChart;
